import React, { useEffect, useState } from 'react';
import Card from 'components/card';
import { FaHeart } from 'react-icons/fa';
import { MdClose } from 'react-icons/md';
import { toast } from 'react-toastify';
import {
    getCustomerLikedProducts,
    toggleCustomerLikedProduct,
} from 'interfaces/customer';
import { getProducts } from 'interfaces/products';

interface LikedProduct {
    _id: string;
    name: string;
    ref?: string;
    unity?: string;
}

export default function LikedProducts(props: { customerId: string }) {
    const { customerId } = props;
    const [likedProductIds, setLikedProductIds] = useState<string[]>([]);
    const [likedProducts, setLikedProducts] = useState<LikedProduct[]>([]);
    const [allProducts, setAllProducts] = useState<LikedProduct[]>([]);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);

    const fetchLikedProducts = async () => {
        setLoading(true);
        const res = await getCustomerLikedProducts(customerId);
        if (res) {
            setLikedProductIds(res.likedProducts || []);
        }
        setLoading(false);
    };

    const fetchAllProducts = async () => {
        const res = await getProducts(1, 5000, null);
        if (res && res.data) {
            setAllProducts(res.data);
        }
    };

    useEffect(() => {
        fetchLikedProducts();
        fetchAllProducts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customerId]);

    // Resolve liked product details from allProducts
    useEffect(() => {
        if (allProducts.length === 0) return;
        const resolved = likedProductIds
            .map((id) => allProducts.find((p) => p._id === id))
            .filter(Boolean) as LikedProduct[];
        setLikedProducts(resolved);
    }, [likedProductIds, allProducts]);

    const handleToggle = async (productId: string) => {
        const isLiked = likedProductIds.includes(productId);
        // Optimistic update
        if (isLiked) {
            setLikedProductIds((prev) => prev.filter((id) => id !== productId));
        } else {
            setLikedProductIds((prev) => [...prev, productId]);
        }

        const res = await toggleCustomerLikedProduct(customerId, productId);
        if (res) {
            setLikedProductIds(res.likedProducts || []);
            toast.info(res.liked ? 'Produit ajouté aux favoris.' : 'Produit retiré des favoris.');
        } else {
            // Rollback
            setLikedProductIds((prev) =>
                isLiked ? [...prev, productId] : prev.filter((id) => id !== productId)
            );
            toast.error('Erreur lors de la mise à jour des favoris.');
        }
    };

    // Search suggestions (products not yet liked)
    const suggestions = search.length >= 1
        ? allProducts
            .filter((p) => !likedProductIds.includes(p._id))
            .filter((p) =>
                p.name?.toLowerCase().includes(search.toLowerCase()) ||
                p.ref?.toLowerCase().includes(search.toLowerCase())
            )
            .slice(0, 8)
        : [];

    return (
        <Card extra="w-full !p-4 border col-span-12 sm:col-span-6">
            <div className="flex items-center gap-2 mb-3">
                <FaHeart className="text-pink-500" />
                <h4 className="text-lg font-medium text-navy-700 dark:text-white">
                    Produits favoris
                </h4>
                <span className="ml-auto text-xs text-gray-400">
                    {likedProducts.length} favori{likedProducts.length > 1 ? 's' : ''}
                </span>
            </div>

            {/* Liked products list */}
            {loading ? (
                <p className="text-sm text-gray-400">Chargement...</p>
            ) : likedProducts.length === 0 ? (
                <p className="text-sm text-gray-500 italic mb-3">
                    Aucun produit favori pour ce client.
                </p>
            ) : (
                <div className="flex flex-wrap gap-2 mb-3">
                    {likedProducts.map((product) => (
                        <div
                            key={product._id}
                            className="flex items-center gap-1.5 bg-pink-50 border border-pink-200 text-pink-800 rounded-full px-3 py-1 text-xs font-medium"
                        >
                            <FaHeart className="text-pink-400" size={10} />
                            <span>{product.name}</span>
                            {product.ref && (
                                <span className="text-pink-400">({product.ref})</span>
                            )}
                            <button
                                onClick={() => handleToggle(product._id)}
                                className="ml-1 text-pink-400 hover:text-pink-600 transition-colors"
                                title="Retirer des favoris"
                            >
                                <MdClose size={14} />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {/* Add product search */}
            <div className="relative">
                <input
                    type="text"
                    placeholder="Ajouter un produit favori..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-400"
                />
                {suggestions.length > 0 && (
                    <div className="absolute z-10 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {suggestions.map((product) => (
                            <button
                                key={product._id}
                                className="w-full text-left px-3 py-2 text-sm hover:bg-pink-50 flex items-center gap-2 transition-colors"
                                onClick={() => {
                                    handleToggle(product._id);
                                    setSearch('');
                                }}
                            >
                                <FaHeart className="text-pink-300" size={10} />
                                <span className="font-medium">{product.name}</span>
                                {product.ref && (
                                    <span className="text-gray-400 text-xs">{product.ref}</span>
                                )}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </Card>
    );
}
